import { Component, EventEmitter, Injector, Input, OnInit, Output, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { ApplicationContext } from 'src/app/models/application-context.model';
import { CdRequestService } from 'src/app/services/cd-request.service';
import { NotificationService } from 'src/app/services/notification.service';
import { ApplicationContextBoundComponent } from 'src/app/shared/components/application-context-bound.component';
import Swal from 'sweetalert2';
import { CorrIcdWizardComponent } from '../../corr-icd-wizard.component';

export interface ReviewSubmitFormData {
  additionalComments: string;
}

@Component({
  selector: 'review-submit',
  templateUrl: './review-submit.component.html',
  styleUrls: ['./review-submit.component.scss'],
  standalone: false
})
export class ReviewSubmitComponent extends ApplicationContextBoundComponent implements OnInit {

  @ViewChild('reviewSubmitForm') reviewSubmitForm: NgForm;

  @Input() parentWizard: CorrIcdWizardComponent | null = null;

  @Output() executionStarted = new EventEmitter<void>();
  @Output() submissionSuccess = new EventEmitter<void>();

  formData: ReviewSubmitFormData = {
    additionalComments: ''
  };

  isSubmitting: boolean = false;
  isSavingDraft: boolean = false;

  constructor(
    protected readonly injector: Injector,
    private readonly _cdRequestService: CdRequestService,
    private readonly _notificationService: NotificationService
  ) {
    super(injector);
  }

  ngOnInit(): void {
  }

  protected override onContextReady(context: ApplicationContext): void {
    super.onContextReady(context);
  }

  validateForm(): boolean {
    if (this.reviewSubmitForm) {
      this.reviewSubmitForm.form.markAllAsTouched();
      return this.reviewSubmitForm.form.valid;
    }
    return true;
  }

  getFormData(): ReviewSubmitFormData {
    return { ...this.formData };
  }

  onSaveDraft(): void {
    if (this.isSubmitting || this.isSavingDraft) {
      return;
    }

    const loanId = this.getLoanId();
    if (!loanId) {
      this._notificationService.showError('Unable to save draft: Loan ID not found', 'Error');
      return;
    }

    this.isSavingDraft = true;
    this.executionStarted.emit();

    const collectedData = this.parentWizard ? this.parentWizard.collectAllFormData() : null;
    const data = collectedData && this.parentWizard ? this.parentWizard.mapFormDataToModel(collectedData) : {
      additionalComments: this.formData.additionalComments
    };

    this._cdRequestService.saveCorrIcdRequestDraft(loanId, data)
      .pipe(finalize(() => this.isSavingDraft = false))
      .subscribe({
        next: () => {
          this._notificationService.showSuccess('Draft saved successfully', 'Success');
        },
        error: (error) => {
          const errorMessage = error?.error?.message || error?.message || 'Failed to save draft';
          this._notificationService.showError(errorMessage, 'Error');
        }
      });
  }

  onSubmitRequest(): void {
    if (this.isSubmitting || this.isSavingDraft) {
      return;
    }

    this.showSubmitConfirmation();
  }

  private showSubmitConfirmation(): void {
    Swal.fire({
      title: 'Are you sure?',
      text: `You are about to submit the ${this.parentWizard?.wizardTitle || 'request'}. This action cannot be undone.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Submit Request',
      cancelButtonText: 'Cancel',
      reverseButtons: true
    }).then((result) => {
      if (result.isConfirmed) {
        this.submitRequest();
      }
    });
  }

  private submitRequest(): void {
    const loanId = this.getLoanId();
    if (!loanId) {
      this._notificationService.showError('Unable to submit request: Loan ID not found', 'Error');
      return;
    }

    this.isSubmitting = true;
    this.executionStarted.emit();

    const collectedData = this.parentWizard ? this.parentWizard.collectAllFormData() : null;
    const data = collectedData && this.parentWizard ? this.parentWizard.mapFormDataToModel(collectedData) : {
      additionalComments: this.formData.additionalComments
    };

    this._cdRequestService.submitCorrIcdRequest(loanId, data)
      .pipe(finalize(() => this.isSubmitting = false))
      .subscribe({
        next: (result) => {
          const docTypeName = result?.generatedDocument?.documentType?.documentTypeName;
          const locationText = docTypeName
            ? `\n\nThe document can be found in Loan Docs under "${docTypeName}".`
            : '';
          Swal.fire('Success!', `${this.parentWizard?.wizardTitle || 'Request'} has been submitted successfully.${locationText}`, 'success');
          this.submissionSuccess.emit();
          if (this.reviewSubmitForm) {
            this.reviewSubmitForm.form.disable();
          }
        },
        error: (error) => {
          const errorMessage = error?.error?.message || error?.message || `Failed to submit ${this.parentWizard?.wizardTitle || 'request'}`;
          this._notificationService.showError(errorMessage, 'Error');
        }
      });
  }

  private getLoanId(): number | null {
    if (this.applicationContext?.application?.applicationId) {
      return this.applicationContext.application.applicationId;
    }
    return null;
  }
}
